// RevenueCat (StoreKit / Apple IAP) thin wrapper.
//
// Every export is a no-op off iOS native (or when NATIVE_IAP_ENABLED is off),
// gated by isNativeIapEnabled(). The web build never reaches the plugin, and
// Android currently sells via Asaas, so this stays iOS-only by design.
//
// Apple 3.1.1: this is the COMPLIANT in-app purchase path. It is independent
// of isPaymentUiBlocked(), which keeps the EXTERNAL (Asaas/aivyv.com) payment
// surfaces hidden on iOS.
//
// CRITICAL: app_user_id MUST be the Supabase user.id. The revenuecat-webhook
// resolves the VYV user by RevenueCat's app_user_id, so logging in with the
// Supabase id is what lets the Apple entitlement converge onto the same
// `subscriptions` row the Asaas webhook writes.
//
// All calls are wrapped in try/catch that ALWAYS console.error before
// swallowing — never a silent catch (CLAUDE.md 4.7).

import {
  Purchases,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
} from "@revenuecat/purchases-capacitor";
import type {
  PurchasesOffering,
  PurchasesPackage,
  CustomerInfo,
} from "@revenuecat/purchases-capacitor";
import { isNativeIapEnabled } from "@/lib/platform";

// RevenueCat entitlement identifier that maps to VYV Premium. Configured in
// the RevenueCat dashboard; the webhook flips subscriptions.plan='premium'
// off the same entitlement, so this string is the contract between client
// and server.
export const PREMIUM_ENTITLEMENT_ID = "premium";

function isUserCancelled(err: unknown): boolean {
  const e = err as { code?: string; userCancelled?: boolean | null } | null;
  return (
    e?.userCancelled === true ||
    e?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR
  );
}

function hasPremiumEntitlement(info: CustomerInfo | null | undefined): boolean {
  return !!info?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID];
}

// Public Apple SDK key — safe to embed in the client (it is not a secret).
// Overridable per-environment via VITE_REVENUECAT_IOS_KEY without a rebuild.
const IOS_API_KEY =
  (import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined) ??
  "appl_WJyfvpKGScjUHRKmXFSUmfzDRNd";

let configured = false;

/**
 * Configure the RevenueCat SDK once at app boot. No-op off iOS native / when
 * the flag is off, or if already configured (idempotent across re-mounts).
 */
export async function initRevenueCat(): Promise<void> {
  if (!isNativeIapEnabled()) return;
  if (configured) return;
  try {
    if (import.meta.env.DEV) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }
    await Purchases.configure({ apiKey: IOS_API_KEY });
    configured = true;
  } catch (err) {
    console.error("[revenuecat] configure failed:", err);
  }
}

/**
 * Identify the current user to RevenueCat. `userId` MUST be the Supabase
 * user.id so the webhook can map the Apple entitlement back to VYV.
 */
export async function rcLogIn(userId: string): Promise<void> {
  if (!isNativeIapEnabled()) return;
  if (!userId) {
    console.error("[revenuecat] rcLogIn called without a userId — skipping");
    return;
  }
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch (err) {
    console.error("[revenuecat] logIn failed:", err);
  }
}

/**
 * Reset RevenueCat to an anonymous app_user_id on sign-out.
 */
export async function rcLogOut(): Promise<void> {
  if (!isNativeIapEnabled()) return;
  try {
    await Purchases.logOut();
  } catch (err) {
    console.error("[revenuecat] logOut failed:", err);
  }
}

/**
 * Fetch the Premium offering (the dashboard `current`, falling back to the
 * `default` offering) which carries the monthly + annual packages. Returns
 * null off iOS native / when the flag is off, or on any error.
 */
export async function getPremiumOfferings(): Promise<PurchasesOffering | null> {
  if (!isNativeIapEnabled()) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings.current ?? offerings.all["default"] ?? null;
  } catch (err) {
    console.error("[revenuecat] getOfferings failed:", err);
    return null;
  }
}

/**
 * Fetch the CREDITS offering (consumable credit packs). Reads
 * offerings.all["credits"] EXPLICITLY — NOT `current` (that's the Premium
 * subscription offering, and `credits` is a non-default offering by design).
 * Returns null off iOS native / when the flag is off, or on any error.
 */
export async function getCreditOfferings(): Promise<PurchasesOffering | null> {
  if (!isNativeIapEnabled()) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings.all["credits"] ?? null;
  } catch (err) {
    console.error("[revenuecat] getOfferings (credits) failed:", err);
    return null;
  }
}

export interface PurchaseOutcome {
  success: boolean;
  cancelled: boolean;
  customerInfo: CustomerInfo | null;
}

/**
 * Purchase a Premium package via StoreKit. A user cancel is NOT an error:
 * it returns { success: false, cancelled: true } without logging. The actual
 * entitlement unlock arrives out-of-band (RevenueCat webhook → subscriptions
 * → PremiumContext realtime), so callers should close on success and let the
 * realtime update flip access — not block waiting on it.
 */
export async function purchasePremium(
  pkg: PurchasesPackage,
): Promise<PurchaseOutcome> {
  if (!isNativeIapEnabled()) {
    return { success: false, cancelled: false, customerInfo: null };
  }
  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return {
      success: hasPremiumEntitlement(result.customerInfo),
      cancelled: false,
      customerInfo: result.customerInfo,
    };
  } catch (err) {
    if (isUserCancelled(err)) {
      return { success: false, cancelled: true, customerInfo: null };
    }
    console.error("[revenuecat] purchasePackage failed:", err);
    return { success: false, cancelled: false, customerInfo: null };
  }
}

/**
 * Purchase a CONSUMABLE credit package via StoreKit. Unlike purchasePremium,
 * success is NOT gated on an entitlement — credit packs grant NO entitlement, so
 * FREE users can buy. `success` means the StoreKit transaction completed; the
 * actual credit grant is server-side (revenuecat-webhook → grant_credits_atomic),
 * so callers must NOT credit anything locally — only refresh the balance. A user
 * cancel returns { cancelled: true } without logging.
 */
export async function purchaseConsumable(
  pkg: PurchasesPackage,
): Promise<PurchaseOutcome> {
  if (!isNativeIapEnabled()) {
    return { success: false, cancelled: false, customerInfo: null };
  }
  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return { success: true, cancelled: false, customerInfo: result.customerInfo };
  } catch (err) {
    if (isUserCancelled(err)) {
      return { success: false, cancelled: true, customerInfo: null };
    }
    console.error("[revenuecat] purchaseConsumable failed:", err);
    return { success: false, cancelled: false, customerInfo: null };
  }
}

/**
 * Restore previous purchases. Returns whether the Premium entitlement is
 * active after the restore (so the UI can say "restored" vs "nothing to
 * restore"). No-op off iOS native.
 */
export async function restorePremium(): Promise<{ restored: boolean }> {
  if (!isNativeIapEnabled()) return { restored: false };
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return { restored: hasPremiumEntitlement(customerInfo) };
  } catch (err) {
    console.error("[revenuecat] restorePurchases failed:", err);
    return { restored: false };
  }
}
